import React, { Component } from 'react';
import PropTypes from 'prop-types';

class AnimatedCounter extends Component {
    constructor(props) {
        super(props);
        this.state = {
            currentValue: props.start || 0,
            hasAnimated: false,
        };
        this.observer = null;
        this.elementRef = null;
        this.animationFrameId = null;
        this.startTime = null;
    }

    componentDidMount() {
        if (typeof window !== 'undefined') {
            const options = {
                root: null,
                rootMargin: '0px',
                threshold: 0.1,
            };

            if ('IntersectionObserver' in window) {
                this.observer = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting && !this.state.hasAnimated) {
                            this.startCounting();
                            this.setState({ hasAnimated: true });
                            if (this.observer && this.elementRef) {
                                this.observer.unobserve(this.elementRef);
                            }
                        }
                    });
                }, options);

                if (this.elementRef) {
                    this.observer.observe(this.elementRef);
                }
            } else {
                // Fallback for older browsers or environments
                this.startCounting();
            }
        }
    }

    componentWillUnmount() {
        if (this.observer && this.elementRef) {
            this.observer.unobserve(this.elementRef);
        }
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
        }
    }

    startCounting = () => {
        this.startTime = null;
        this.animationFrameId = requestAnimationFrame(this.animate);
    };

    animate = (timestamp) => {
        if (!this.startTime) this.startTime = timestamp;
        const progress = timestamp - this.startTime;
        const duration = this.props.duration || 2000;
        const start = this.props.start || 0;
        const end = this.props.end || 0;

        if (progress < duration) {
            // Easing function: easeOutQuad (decelerating to zero velocity)
            const t = progress / duration;
            const easeOutQuad = t * (2 - t);
            const current = start + (end - start) * easeOutQuad;
            
            this.setState({ currentValue: current });
            this.animationFrameId = requestAnimationFrame(this.animate);
        } else {
            this.setState({ currentValue: end });
        }
    };

    formatValue = (value) => {
        const { decimals, formatter, prefix, suffix } = this.props;

        if (formatter) {
            return formatter(value);
        }

        let formatted = value.toFixed(decimals || 0);
        
        // Add thousands separator (e.g. 1,000)
        const parts = formatted.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        formatted = parts.join('.');

        return `${prefix || ''}${formatted}${suffix || ''}`;
    };

    render() {
        const { currentValue } = this.state;
        const { className, style } = this.props;

        return (
            <span 
                ref={el => { this.elementRef = el; }} 
                className={className} 
                style={style}
            >
                {this.formatValue(currentValue)}
            </span>
        );
    }
}

AnimatedCounter.propTypes = {
    start: PropTypes.number,
    end: PropTypes.number.isRequired,
    duration: PropTypes.number,
    decimals: PropTypes.number,
    prefix: PropTypes.string,
    suffix: PropTypes.string,
    formatter: PropTypes.func,
    className: PropTypes.string,
    style: PropTypes.object,
};

AnimatedCounter.defaultProps = {
    start: 0,
    duration: 2000,
    decimals: 0,
    prefix: '',
    suffix: '',
};

export default AnimatedCounter;
